import React, { useEffect, useState, useContext, useCallback } from "react";
import { Formik, Form, Field, getIn, ErrorMessage } from "formik";
import * as Yup from "yup";
import moment from "moment";

import DispatchContext from "../../contexts/DispatchContext";
import DataFetcher from "../../utils/DataFetcher";
import ErrorLine from "../elements/ErrorLine";
import LoadingIndicator from "../elements/LoadingIndicator";

import formdata from "../../data/formdata";
import { convertSpaces, compareValues, dispError, dispSuccess } from "../../utils/Tools";

function FormLicencesGenerate(props) {
  const appDispatch = useContext(DispatchContext);

  const [maxLicences, setMaxLicences] = useState(null);
  const [selection, setSelection] = useState(null); // Institutions dropdown
  const [institutionID, setInstID] = useState(null);

  const targetURLs = {
    "lizensierender-mandant": {
      getURL: "/client/institution-licencing-list",
      getPayload: {}
    },
    "testender-mandant": {
      getURL: "/institution/detail",
      getPayload: {}
    },
    "institution-admin": {
      getURL: "/institution/detail",
      getPayload: {}
    }
  };

  const loadData = useCallback(() => {
    DataFetcher.fetch(targetURLs[props.usertype].getURL, targetURLs[props.usertype].getPayload)
      .then(response => {
        if (!response.success) {
          return appDispatch(dispError(response.msg));
        }
        setMaxLicences(response.licences.available);
        if (props.usertype === "lizensierender-mandant") setSelection(response.list);
        if (props.usertype === "testender-mandant") setInstID(response.institution._id);
      })
      .catch(error => {
        console.error(error);
      });
  }, [appDispatch, props, targetURLs]);

  useEffect(() => {
    if (!maxLicences) {
      loadData();
    }
  }, [maxLicences, loadData]);

  const yupObject = {
    valid_until: Yup.date().min(moment().add(1, "D"), "Bitte geben Sie ein gültiges Datum ein.").required("Bitte geben Sie ein Datum ein.")
  };
  if (props.usertype === "lizensierender-mandant") {
    yupObject.institution_id = Yup.string().required("Bitte wählen Sie eine Institution aus.");
  }
  if (props.usertype !== "lizensierender-mandant") {
    yupObject.testgroup = Yup.string()
      .matches(/^[a-zA-Z0-9-_]*$/g, "Testgruppen dürfen nur die Zeichen von A-Z, a-z, 0-9, - und _ enthalten.")
      .required("Bitte wählen Sie eine Testgruppe aus.");
    yupObject.presets = Yup.object().shape({
      region: Yup.string().required("Bitte wählen Sie ein Bundesland aus."),
      graduation: Yup.string().required("Bitte wählen Sie einen Schulabschluss aus.")
    });
  }

  if (maxLicences) {
    yupObject.licencecount = Yup.number().min(1, "Bitte geben Sie die gewünschte Lizenzmenge an.").max(maxLicences, `Es stehen nur ${maxLicences} Lizenzen zur Verfügung.`).required("Bitte geben Sie die gewünschte Lizenzmenge an.");
  } else {
    yupObject.licencecount = Yup.number().min(1, "Bitte geben Sie die gewünschte Lizenzmenge an.").required("Bitte geben Sie die gewünschte Lizenzmenge an.");
  }

  const defaultValues = {
    valid_until: moment().add(3, "M").format("YYYY-MM-DD"),
    licencecount: 0
  };
  if (props.usertype === "lizensierender-mandant") {
    defaultValues.institution_id = "";
  }
  if (props.usertype !== "lizensierender-mandant") {
    defaultValues.testgroup = "";
    defaultValues.presets = {
      region: "",
      graduation: ""
    };
  }

  const onSubmit = (values, { resetForm, setSubmitting }) => {
    let payload = { ...values };
    if (institutionID) payload.institution_id = institutionID;
    let confirm = window.confirm(`Wollen Sie wirklich ${payload.licencecount} Lizenzen generieren?`);
    if (confirm) {
      DataFetcher.fetch("/licence/generate-batch", payload)
        .then(response => {
          if (!response.success) {
            setSubmitting(false);
            return appDispatch(dispError(response.msg));
          }
          appDispatch(dispSuccess(response.msg));
          resetForm({ values: "" });
          loadData();
        })
        .catch(error => {
          console.error(error);
          setSubmitting(false);
        });
    } else {
      setSubmitting(false);
    }
  };

  const getGraduations = id => {
    return formdata.graduations[id].map((item, index) => {
      return (
        <option key={`grad-${index}`} value={item[0]}>
          {item[1]}
        </option>
      );
    });
  };

  return (
    <>
      {maxLicences === null && <LoadingIndicator />}
      {!Boolean(maxLicences) && <p>Es stehen keine Lizenzen zur Verfügung</p>}
      {Boolean(maxLicences) && (
        <Formik initialValues={defaultValues} validationSchema={Yup.object(yupObject)} onSubmit={onSubmit}>
          {({ values, errors, touched, isSubmitting, handleChange, handleBlur, setFieldValue }) => (
            <Form autoComplete="off">
              {props.usertype === "lizensierender-mandant" && selection && selection.length > 0 && (
                <div className="field">
                  <label className="label">Institution*</label>
                  <div className="control">
                    <div className={`select${touched.institution_id && errors.institution_id ? " is-danger" : ""}`}>
                      <Field as="select" name="institution_id">
                        <option>Wählen</option>
                        {selection.sort(compareValues("name")).map((item, index) => {
                          let disabled = false;
                          if (!item.isActivated || item.isDeleted) disabled = true;
                          return (
                            <option key={`inst-${index}`} value={item._id} disabled={disabled}>
                              {item.name}
                            </option>
                          );
                        })}
                      </Field>
                    </div>
                  </div>
                  <ErrorMessage component={ErrorLine} name="institution_id" />
                </div>
              )}

              {props.usertype !== "lizensierender-mandant" && (
                <>
                  <div className="field">
                    <label className="label">Name Gruppe/Klasse*</label>
                    <div className="control">
                      <Field
                        name="testgroup"
                        type="text"
                        onBlur={e => {
                          e.target.value = values.testgroup = convertSpaces(e.target.value);
                          handleBlur(e);
                        }}
                        className={`input${touched.testgroup && errors.testgroup ? " is-danger" : ""}`}
                      />
                    </div>
                    <ErrorMessage component={ErrorLine} name="testgroup" />
                  </div>
                  <div className="field">
                    <label className="label">Bundesland*</label>
                    <div className="control">
                      <div className={`select${getIn(touched, "presets.region") && getIn(errors, "presets.region") ? " is-danger" : ""}`}>
                        <Field
                          as="select"
                          name="presets.region"
                          onChange={e => {
                            handleChange(e);
                            setFieldValue("presets.graduation", "");
                          }}>
                          <option value="">Wählen</option>
                          {formdata.federal_states.map((item, index) => {
                            return (
                              <option key={`region-${index}`} value={item[0]}>
                                {item[1]}
                              </option>
                            );
                          })}
                        </Field>
                      </div>
                    </div>
                    <ErrorMessage component={ErrorLine} name="presets.region" />
                  </div>
                  <div className="field">
                    <label className="label">Schulabschluss*</label>
                    <div className="control">
                      <div className={`select${getIn(touched, "presets.graduation") && getIn(errors, "presets.graduation") ? " is-danger" : ""}`}>
                        <Field as="select" name="presets.graduation" disabled={!Boolean(getIn(values, "presets.region"))}>
                          <option value="">Wählen</option>
                          {Boolean(getIn(values, "presets.region")) && getGraduations(getIn(values, "presets.region"))}
                        </Field>
                      </div>
                    </div>
                    <ErrorMessage component={ErrorLine} name="presets.graduation" />
                  </div>
                </>
              )}

              <div className="field">
                <label className="label">Anzahl Lizenzen* (noch {maxLicences} verfügbare Lizenzen)</label>
                <div className="control">
                  <Field name="licencecount" type="number" max={maxLicences} className={`input${touched.licencecount && errors.licencecount ? " is-danger" : ""}`} />
                </div>
                <ErrorMessage component={ErrorLine} name="licencecount" />
              </div>

              <div className="field">
                <label className="label">Gültig bis*</label>
                <div className="control">
                  <Field name="valid_until" type="date" className={`input${touched.valid_until && errors.valid_until ? " is-danger" : ""}`} />
                </div>
                <ErrorMessage component={ErrorLine} name="valid_until" />
              </div>

              <div className="field">
                <div className="control field-body">
                  <button className="button is-success" type="submit" disabled={isSubmitting}>
                    Absenden
                  </button>
                  <button className="button ml-2" type="button" onClick={props.onCancel}>
                    Abbrechen
                  </button>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      )}
    </>
  );
}
export default FormLicencesGenerate;
